// --- PKCE + OIDC helpers ---

const toBase64Url = (arrayBuf) => {
  let str = btoa(String.fromCharCode(...new Uint8Array(arrayBuf)));
  return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const randomString = (len = 64) => {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => ('0' + b.toString(16)).slice(-2)).join('');
};

const sha256Base64Url = async (input) => {
  const enc = new TextEncoder();
  const data = enc.encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return toBase64Url(hash);
};

/**
 * Fetch OIDC discovery document from issuer.
 * issuerUri: e.g. "https://idp.example.com/realms/finland"
 */
export const fetchOpenIdConfiguration = async (issuerUri) => {
  const wellKnown =
    issuerUri.replace(/\/+$/, '') + '/.well-known/openid-configuration';
  const res = await fetch(wellKnown, { cache: 'no-store' });
  if (!res.ok) throw new Error('Discovery failed: ' + res.status);

  return res.json();
};

/**
 * Build an authorization URL with PKCE.
 * opts: { authorizationEndpoint, clientId, redirectUri, scopes: string[], extraParams?: object }
 * Returns { loginUrl, codeVerifier, state }
 */
export const buildAuthorizationUrlWithPkce = async (opts) => {
  const {
    authorizationEndpoint,
    clientId,
    redirectUri,
    scopes = ['openid'],
    extraParams = {}
  } = opts;

  const state = crypto.randomUUID();
  const codeVerifier = randomString(64);
  const codeChallenge = await sha256Base64Url(codeVerifier);

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: scopes.join(' '),
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256'
  });

  // Optional extras (e.g., acr_values, audience, domain_hint, kc_idp_hint, etc.)
  for (const [k, v] of Object.entries(extraParams)) {
    if (v != null) params.append(k, String(v));
  }

  return {
    loginUrl: authorizationEndpoint + '?' + params.toString(),
    codeVerifier,
    state
  };
};
